import { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./courier.module.css";
import {
  useGetUserDetailsQuery,
  useUpdateUserMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { colors } from "../../theme/colors";
import { formatCourierName } from "./utils";

type CourierDetailsLocationState = {
  source?: string;
  userType?: string;
};

/**
 * Safely extracts courier details routing metadata from location state.
 *
 * Args:
 *   state: Router location state value.
 *
 * Returns:
 *   A normalized state object when valid; otherwise null.
 */
const getCourierDetailsLocationState = (
  state: unknown
): CourierDetailsLocationState | null => {
  if (!state || typeof state !== "object") {
    return null;
  }
  const { source, userType } = state as {
    source?: unknown;
    userType?: unknown;
  };
  return {
    source: typeof source === "string" ? source : undefined,
    userType: typeof userType === "string" ? userType : undefined,
  };
};

/**
 * Displays read-only details for a single courier user.
 *
 * Notes:
 * - Skips fetching when the route param id is missing.
 * - TODO: Confirm whether deactivation fields should be shown for all roles.
 *
 * Returns:
 *   The courier details page component.
 */
const CourierDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = getCourierDetailsLocationState(location.state);
  const isUserDetailsRoute = location.pathname.startsWith("/users/");
  const shouldUseUserEdit =
    locationState?.source === "USER_DETAILS" || isUserDetailsRoute;

  useEffect(() => {
    // Guard against invalid routes by returning to the previous page.
    if (!id) {
      navigate(-1);
    }
  }, [id, navigate]);

  // Avoid firing a detail request when the route is malformed.
  // Skip fetching when the route param is missing to avoid a bad request.
  const [activateModalVisible, setActivateModalVisible] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const { data: courierDetails, isLoading } = useGetUserDetailsQuery(id ?? "", {
    skip: !id,
  });
  const [updateUser] = useUpdateUserMutation();
  const displayEmail = (courierDetails)?.mail || "";
  const displayPhone = (courierDetails)?.phone || "";

  const handleActivate = async () => {
    if (!courierDetails?.id) {
      return;
    }
    try {
      setIsActivating(true);
      await updateUser({
        id: courierDetails.id,
        is_active: true,
        deactivate_reason: "",
      }).unwrap();
      setActivateModalVisible(false);
    } catch (error: any) {
      setActivateModalVisible(false);
      setMessage({
        type: "error",
        text: error?.message || error?.data?.detail || "Something went wrong!",
      });
    } finally {
      setIsActivating(false);
    }
  };

  if (!id) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "30px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          height: "100%",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "calc(100vh - 97px)",
            }}
          >
            <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
          </Box>
        ) : (
          <Box sx={{ height: "100%", padding: "0px 20px" }}>
            <Typography className={style.courierDetailHeading}>
              Courier Details
            </Typography>
            <Box>
              <Box className={style.detailsRow}>
                <Typography className={style.title}>Full Name</Typography>
                <Typography className={style.value}>
                  {formatCourierName(courierDetails)}
                </Typography>
              </Box>
              {courierDetails?.title && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Title</Typography>
                  <Typography className={style.value}>
                    {courierDetails?.title}
                  </Typography>
                </Box>
              )}
              <Typography className={style.courierDetailHeading}>
                Contact Details
              </Typography>
              {displayPhone && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Mobile Number</Typography>
                  <Typography className={style.value}>
                    {displayPhone}
                  </Typography>
                </Box>
              )}
              {displayEmail && (
                <Box className={style.detailsRow}>
                  <Typography className={style.title}>Email Address</Typography>
                  <Typography className={style.value}>
                    {displayEmail}
                  </Typography>
                </Box>
              )}

              {courierDetails?.deactivate_reason && (
                <Box>
                  <Typography className={style.courierDetailHeading}>
                    Deactivation Details
                  </Typography>
                  <Box>
                    <Box className={style.detailsRow}>
                      <Typography className={style.title}>
                        Reason for deactivation
                      </Typography>
                      <Typography className={style.value}>
                        {courierDetails?.deactivate_reason}
                      </Typography>
                    </Box>
                    <Box className={style.detailsRow}>
                      <Typography className={style.title}>
                        Date of Deactivation
                      </Typography>
                      <Typography className={style.value}>
                        {courierDetails?.deactivated_at
                          ? dayjs(courierDetails?.deactivated_at).format(
                            "MMMM DD YYYY, hh:mm:ss A"
                          )
                          : "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {message && (
                <Box mt={2}>
                  <LabelBox
                    boxProps={{
                      height: 50,
                      display: "flex",
                      alignItems: "center",
                    }}
                    data={message}
                    onClose={() => setMessage(null)}
                  />
                </Box>
              )}
              <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
                <Button
                  sx={{
                    width: "200px",
                    height: "55px",
                    ":hover": { backgroundColor: "transparent" },
                  }}
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  label={"Close"}
                />
                {courierDetails?.is_active ? (
                  <Button
                    sx={{
                      width: "200px",
                      height: "55px",
                      ":hover": { backgroundColor: colors.primary },
                    }}
                    variant="contained"
                    label={"Edit"}
                    disabled={!courierDetails?.id}
                    onClick={() => {
                      if (!courierDetails?.id) {
                        return;
                      }
                      if (shouldUseUserEdit) {
                        navigate(`/users/edit/${courierDetails.id}`, {
                          state: { userType: "COURIER" },
                        });
                        return;
                      }
                      navigate(`/couriers/edit/${courierDetails.id}`);
                    }}
                  />
                ) : (
                  <Button
                    sx={{
                      width: "200px",
                      height: "55px",
                      ":hover": { backgroundColor: colors.primary },
                    }}
                    variant="contained"
                    label={"Activate"}
                    disabled={!courierDetails?.id}
                    onClick={() => setActivateModalVisible(true)}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      {activateModalVisible && (
        <ConfirmationDialog
          visible={activateModalVisible}
          message={`Are you sure you want to activate "${formatCourierName(courierDetails)}"?`}
          onClose={() => setActivateModalVisible(false)}
          onConfirm={handleActivate}
          loading={isActivating}
        />
      )}
    </Box>
  );
};

export default CourierDetails;
